import React, { useCallback } from "react";

import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import { showModal } from "@mapmycustomers/ui";

import BottomNavigationBar from "@app/component/BottomNavigationBar";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";

import IntegrationEditStep from "../../enum/IntegrationEditStep";

import styles from "./ButtonBar.module.scss";

interface Props {
  analyticIssuer?: AnalyticsService;
  areIntegrationFieldsOrUsersChanged: boolean;
  currentStep: IntegrationEditStep;
  isNewIntegration: boolean;
  isTrial?: boolean;
  nextButtonErrorMessage?: string;
  nextDisabled?: boolean;
  onNextClick: () => void;
  onPreviousClick: () => void;
}

const ButtonBar: React.FC<Props> = ({
  analyticIssuer,
  areIntegrationFieldsOrUsersChanged,
  currentStep,
  isNewIntegration,
  isTrial,
  nextButtonErrorMessage,
  nextDisabled,
  onNextClick,
  onPreviousClick,
}) => {
  const intl = useIntl();
  const history = useHistory();

  const handleCancel = useCallback(() => {
    analyticIssuer?.clicked(["Cancel Integration"]);
    history.replace(`${Path.SETTINGS}/${SettingPath.ORGANIZATION_INTEGRATIONS}`);
  }, [analyticIssuer, history]);

  const handleNextClick = useCallback(() => {
    if (
      currentStep === IntegrationEditStep.FREQUENCY &&
      !isNewIntegration &&
      !isTrial &&
      areIntegrationFieldsOrUsersChanged
    ) {
      showModal({
        cancelButtonProps: {
          type: "link",
        },
        cancelText: intl.formatMessage({
          id: "integrations.buttonBar.reSaveModal.cancel",
          defaultMessage: "Wait",
          description: "Cancel option in re-save modal on integration button bar",
        }),
        centered: true,
        closable: false,
        content: (
          <div className={styles.modalContent}>
            <FontAwesomeIcon className={styles.warningIcon} icon={faExclamationCircle} size="xl" />
            <div>
              <div className={styles.modalTitle}>
                {intl.formatMessage({
                  id: "integrations.buttonBar.reSaveModal.title",
                  defaultMessage:
                    "A one-time sync of existing data will run prior to desired changes to users and fields.",
                  description: "Title on re-save modal on integration button bar",
                })}
              </div>
              <div className={styles.modalDescription}>
                {intl.formatMessage(
                  {
                    id: "integrations.buttonBar.reSaveModal.description",
                    defaultMessage:
                      "This ensures any pending edits to records are synced before bringing in historic data and no loss of data occurs. <b>Any scheduled syncs will not occur while re-sync is ongoing.</b>",
                    description: "Description on re-save modal on integration button bar",
                  },
                  { b: (text) => <b>{text}</b> }
                )}
              </div>
            </div>
          </div>
        ),
        icon: null,
        maskClosable: false,
        okText: intl.formatMessage({
          id: "integrations.buttonBar.reSaveModal.ok",
          defaultMessage: "Save and Start Sync",
          description: "Ok option in re-save modal on integration button bar",
        }),
        onOk: onNextClick,
        type: "confirm",
        width: 550,
      });
    } else {
      onNextClick();
    }
  }, [
    areIntegrationFieldsOrUsersChanged,
    currentStep,
    intl,
    isNewIntegration,
    isTrial,
    onNextClick,
  ]);

  return (
    <BottomNavigationBar
      cancelButtonTitle={
        isNewIntegration
          ? intl.formatMessage({
              id: "integrations.buttonBar.cancel",
              defaultMessage: "Cancel Integration",
              description: "Text of cancel integration button",
            })
          : intl.formatMessage({
              id: "integrations.buttonBar.exit",
              defaultMessage: "Exit Integration",
              description: "Text of exit integration button",
            })
      }
      nextButtonErrorMessage={nextButtonErrorMessage}
      nextButtonTitle={
        !isNewIntegration && currentStep === IntegrationEditStep.FREQUENCY
          ? intl.formatMessage({
              id: "integrations.buttonBar.nextButtonTitle.saveChanges",
              defaultMessage: "Save Changes",
              description: "Text of next button - Save Changes",
            })
          : intl.formatMessage({
              id: "integrations.buttonBar.nextButtonTitle",
              defaultMessage: "Continue to next step",
              description: "Text of next button",
            })
      }
      nextDisabled={nextDisabled}
      onCancelClick={handleCancel}
      onNextClick={handleNextClick}
      onPreviousClick={onPreviousClick}
      previousHidden={currentStep === IntegrationEditStep.CHOOSE}
    />
  );
};

export default ButtonBar;
